import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import DynamicFormPage from "../../components/DynamicFormPage";
import { useQuery } from "@tanstack/react-query";
import { getCategories } from "../../helpers/http_categories_requests";
import { getFormats } from "../../helpers/http_formats_requests";
import { getComforts } from "../../helpers/http_comforts_requests";
import { getPlatings } from "../../helpers/http_platings_requests";
import { getExternalFinishings } from "../../helpers/http_external_finishings_requests";
import { getProducts } from "../../helpers/http_products_requests";
import { getOrderDetails } from "../../helpers/http_orders_requests";
import { updateProduction } from "../../helpers/http_production_requests";
import { useCatalog } from "../../providers/CatalogProvider";
import { getStoneVariations } from "../../helpers/http_stone_variations_requests";
import { formatOptionsFromApi } from "../../utils/functions";
import { getStones } from "../../helpers/http_stones_requests";

export default function ProductionDetails() {

    const { id } = useParams();
    const { metalList } = useCatalog();

    const formatOptions = (data) => data?.data?.data?.data?.map(item => ({ value: item.id, label: item.name }));

    const categoriesQuery = useQuery({ queryKey: ['getCategories'], queryFn: getCategories, staleTime: 1000 * 5 });
    const formatsQuery = useQuery({ queryKey: ['getFormats'], queryFn: getFormats, staleTime: 1000 * 5 });
    const comfortsQuery = useQuery({ queryKey: ['getComforts'], queryFn: getComforts, staleTime: 1000 * 5 });
    const platingsQuery = useQuery({ queryKey: ['getPlatings'], queryFn: getPlatings, staleTime: 1000 * 5 });
    const externalFinishingsQuery = useQuery({ queryKey: ['getExternalFinishings'], queryFn: getExternalFinishings, staleTime: 1000 * 5 });
    const productsQuery = useQuery({ queryKey: ['getProducts'], queryFn: getProducts });
    const stonesQuery = useQuery({ queryKey: ['getStones'], queryFn: getStones, staleTime: 1000 * 5 });

    const orderQuery = useQuery({ queryKey: ['getOrderDetails', id], queryFn: () => getOrderDetails(id) });

    const queries = [categoriesQuery, formatsQuery, comfortsQuery, platingsQuery, externalFinishingsQuery, productsQuery, orderQuery, stonesQuery];

    const isFetching = queries.some(query => query.isFetching);

    const categories = formatOptions(categoriesQuery);
    const formats = formatOptions(formatsQuery);
    const comforts = formatOptions(comfortsQuery);
    const platings = formatOptions(platingsQuery);
    const externalFinishings = formatOptions(externalFinishingsQuery);
    const stones = formatOptionsFromApi(stonesQuery);

    const { data: orderData } = orderQuery?.data?.data ?? {};

    const navigate = useNavigate();

    const ringSizeArray = Array.from({ length: 15 }, (_, i) => {
        const value = i + 10;
        return {
            value,
            label: "Número " + value,
        };
    });

    const productFormFieldList = [
        {
            title: "Dados de Produto",
            fields: [
                { name: "name", label: "Nome", type: "text", required: true },
                { name: "category", label: "Categoria", type: "select", required: true, options: categories },
                { name: "selected_metal", label: "Material", type: "select", required: true, options: metalList },
                { name: "format", label: "Formato", type: "select", options: formats },
                { name: "confort", label: "Conforto", type: "select", options: comforts },
                { name: "height", label: "Altura", type: "number", end_adornment: "mm" },
                { name: "width", label: "Largura", type: "number", end_adornment: "mm" },
                { name: "thickness", label: "Espessura", type: "number", end_adornment: "mm" },
                { name: "plating", label: "Banho", type: "select", options: platings },
                { name: "finishing", label: "Acabamento Externo", type: "select", options: externalFinishings },
                { name: "is_crafted", label: "Joia trabalhada / montada", type: "check" },
                { name: "public_description", label: "Descrição Pública", type: "text", multiline: true, rows: 5, fullWidth: true },
                { name: "observation", label: "Observação do cliente", type: "text", multiline: true, rows: 4, fullWidth: true },
            ]
        },
        {
            title: "Tamanhos e Gravações",
            fields: [
                { name: "size_one", label: "Tamanho joia 1", type: "select", options: ringSizeArray },
                { name: "engraving_one", label: "Gravação joia 1", type: "text", },
                { name: "size_two", label: "Tamanho joia 2", type: "select", options: ringSizeArray },
                { name: "engraving_two", label: "Gravação joia 2", type: "text", },
            ]
        },
        {
            title: "Pesos de Produto",
            fields: [
                { name: "high_gold_weight", label: "Peso em Ouro 18K", type: "number", required: true, end_adornment: "g" },
                { name: "low_gold_weight", label: "Peso em Ouro 10K", type: "number", required: true, end_adornment: "g" },
                { name: "silver_weight", label: "Peso em Prata 950", type: "number", required: true, end_adornment: "g" },
            ]
        },
        {
            title: "Pedras de Produto",
            fields: [
                {
                    name: "stones",
                    label: "Pedras",
                    type: "stones",
                    fullWidth: true,
                    modal_props: {
                        title: 'Inclusão de Pedras',
                        fields: [
                            {
                                title: 'Especificações',
                                fields: [
                                    {
                                        name: 'stone',
                                        label: 'Pedra',
                                        type: 'select_object',
                                        required: true,
                                        options: stones,
                                    },
                                    {
                                        name: 'variation',
                                        label: 'Variação',
                                        type: 'select_object',
                                        required: true,
                                        dependsOn: 'stone',
                                        dynamicOptions: (stoneId) => getStoneVariations(stoneId)
                                    },
                                    {
                                        name: 'quantity',
                                        label: 'Quantidade',
                                        type: 'number',
                                        required: true,
                                        end_adornment: 'unidades',
                                    },
                                    {
                                        name: "observation",
                                        label: "Observação",
                                        type: "text",
                                        multiline: true,
                                        rows: 5,
                                        fullWidth: true,
                                    }

                                ]
                            }
                        ]
                    }
                },
            ]
        },
        {
            title: "Ficha Técnica",
            fields: [
                { name: "self_reference", label: "Referência Interna", type: "text", required: true },
                { name: "prod_time", label: "Tempo Ideal de Produção", type: "number", required: true, end_adornment: "dias" },
                { name: "hard_weight_high_gold", label: "Peso Bruto Ouro 18K", type: "number", required: true, end_adornment: "g" },
                { name: "hard_weight_low_gold", label: "Peso Bruto Ouro 10K", type: "number", required: true, end_adornment: "g" },
                { name: "hard_weight_silver", label: "Peso Bruto Prata 950", type: "number", required: true, end_adornment: "g", max_length: 1 },
                { name: "production_description", label: "Descrição para Produção", type: "text", multiline: true, rows: 5, fullWidth: true },
                { name: "prod_steps_json", label: "Passos de Produção", type: "steps", fullWidth: true },
            ]
        },
        {
            title: "Datas",
            fields: [
                { name: "ordered_at", label: "Data de Pedido", type: "date_time_picker", required: true },
                { name: "production_date", label: "Data de Produção", type: "date_time_picker", required: true },
                { name: "delivery_date", label: "Data de Entrega", type: "date_time_picker", required: true },
            ]
        },

    ];

    const serviceFormFieldList = [
        {
            title: "Dados de Produto",
            fields: [
                { name: "name", label: "Nome", type: "text", required: true, disabled: true },
                { name: "selected_metal", label: "Material", type: "select", required: true, options: metalList },
            ]
        },
        {
            title: "Ficha Técnica",
            fields: [
                { name: "self_reference", label: "Referência Interna", type: "text", required: true },
                { name: "prod_time", label: "Tempo Ideal de Produção", type: "number", required: true, end_adornment: "dias" },
                { name: "production_description", label: "Descrição para Produção", type: "text", multiline: true, rows: 5, fullWidth: true },
                { name: "prod_steps_json", label: "Passos de Produção", type: "steps", fullWidth: true },
            ]
        },
        {
            title: "Data de Entrega",
            fields: [
                { name: "delivery_date", label: "Data de Entrega", type: "date_time_picker", required: true },
            ]
        },

    ];


    const formFieldList = orderData?.is_service ? serviceFormFieldList : productFormFieldList;

    return <DynamicFormPage multiImageUpload details isLoading={isFetching} initialData={orderData} title={"Produção - Detalhes"} formFieldList={formFieldList} onCancel={() => navigate(-1)} fixedActions production updateProduction={() => updateProduction(id, () => navigate(-1))} />;
};